import { useRef, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import { describeControlBinding } from "../../controls/controlBindings";
import { controlSessionStore } from "../../controls/controlSession";
import { LearnableControl } from "../LearnableControl";

export type SliderProps = {
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  className?: string;
  disabled?: boolean;
  /** Forwarded for instrument automation / tests. */
  "data-path"?: string;
  "aria-label"?: string;
  title?: string;
  /** When set, right-click Learn MIDI/OSC; bound state paints the thumb amber. */
  learnPath?: string;
};

/**
 * Adam-styled slider — drop-in for dense instrument / FX rows.
 * Thumb is center-aligned to the value (overflows track edges) so dragging
 * does not snap the way Radix’s “contain” offset does.
 */
export function Slider({
  min,
  max,
  step = 1,
  value,
  onChange,
  className,
  disabled,
  "data-path": dataPath,
  "aria-label": ariaLabel,
  title,
  learnPath,
}: SliderProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const dragOffsetRef = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef({ min, max, step, value: clamp(value, min, max) });
  valueRef.current = { min, max, step, value: clamp(value, min, max) };

  const binding = useStoreSelector(controlSessionStore, s =>
    learnPath ? (s.bindings.find(b => b.target === learnPath) ?? null) : null,
  );
  const bound = binding != null;
  const boundTitle = binding ? describeControlBinding(binding.binding) : null;

  const clamped = valueRef.current.value;
  const span = Math.max(max - min, Number.EPSILON);
  const percent = ((clamped - min) / span) * 100;
  const rootClass = ["adam-slider", className].filter(Boolean).join(" ");

  function rawFromClientX(clientX: number): number {
    const root = rootRef.current;
    const { min: lo, max: hi, value: current } = valueRef.current;
    if (!root) {
      return current;
    }
    const rect = root.getBoundingClientRect();
    if (rect.width <= 0) {
      return current;
    }
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return lo + ratio * (hi - lo);
  }

  function commitPointer(clientX: number): void {
    if (disabled) {
      return;
    }
    const { min: lo, max: hi, step: st } = valueRef.current;
    onChangeRef.current(snap(rawFromClientX(clientX) + dragOffsetRef.current, lo, hi, st));
  }

  function endDrag(): void {
    dragOffsetRef.current = 0;
    window.removeEventListener("pointermove", onWindowMove);
    window.removeEventListener("pointerup", onWindowUp);
    window.removeEventListener("pointercancel", onWindowUp);
  }

  function onWindowMove(event: PointerEvent): void {
    commitPointer(event.clientX);
  }

  function onWindowUp(): void {
    endDrag();
  }

  function onPointerDown(event: ReactPointerEvent<HTMLSpanElement>): void {
    if (disabled || event.button !== 0) {
      return;
    }
    event.preventDefault();
    const target = event.target as HTMLElement | null;
    const onThumb = target?.closest?.("[data-adam-slider-thumb]") != null;
    if (onThumb) {
      // Keep grab point: don't snap value to cursor center on press.
      dragOffsetRef.current = clamped - rawFromClientX(event.clientX);
    } else {
      dragOffsetRef.current = 0;
      commitPointer(event.clientX);
    }
    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", onWindowUp);
    window.addEventListener("pointercancel", onWindowUp);
    rootRef.current?.focus({ preventScroll: true });
  }

  function onKeyDown(event: KeyboardEvent<HTMLSpanElement>): void {
    if (disabled) {
      return;
    }
    const key = event.key;
    let next: number | undefined;
    if (key === "ArrowLeft" || key === "ArrowDown") {
      next = clamped - step;
    } else if (key === "ArrowRight" || key === "ArrowUp") {
      next = clamped + step;
    } else if (key === "PageDown") {
      next = clamped - step * 10;
    } else if (key === "PageUp") {
      next = clamped + step * 10;
    } else if (key === "Home") {
      next = min;
    } else if (key === "End") {
      next = max;
    }
    if (next === undefined) {
      return;
    }
    event.preventDefault();
    onChange(snap(next, min, max, step));
  }

  const control = (
    <span
      ref={rootRef}
      className={rootClass}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={clamped}
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? "" : undefined}
      data-bound={bound ? "" : undefined}
      data-path={dataPath}
      title={boundTitle ?? title}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span className="adam-slider-track">
        <span className="adam-slider-range" style={{ width: `${percent}%` }} />
      </span>
      <span
        className="adam-slider-thumb"
        data-adam-slider-thumb=""
        style={{ left: `${percent}%` }}
      />
    </span>
  );

  if (!learnPath) {
    return control;
  }
  return (
    <LearnableControl path={learnPath} boundMark={false} className="block w-full min-w-0">
      {control}
    </LearnableControl>
  );
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
}

function snap(value: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(step) || step <= 0) {
    return clamp(value, min, max);
  }
  const decimals = decimalCount(step);
  const snapped = Math.round((value - min) / step) * step + min;
  return clamp(roundTo(snapped, decimals), min, max);
}

function decimalCount(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const text = String(value);
  const i = text.indexOf(".");
  return i < 0 ? 0 : text.length - i - 1;
}

function roundTo(value: number, decimals: number): number {
  if (decimals <= 0) {
    return Math.round(value);
  }
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
